'use client';

import type React from 'react';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Mail,
  ArrowLeft,
  Loader2,
  CheckCircle,
  User as UserIcon,
  Calendar,
  CreditCard,
  Lock,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  registerSchema,
  type RegisterFormData,
  type ManagerRegistrationPayload,
  Gender,
} from '@/schemas/registerSchema';
import { managerService } from '@/api/manager/managerApi';
import { handleGlobalError, getAxiosErrorStatus } from '@/utils/error-handler';
import { logError } from '@/utils/logger';
import { handleCuitInput } from '@/utils/cuit-utils';
import { GoogleLogin } from '@react-oauth/google';
import { setAuthTokens } from '@/lib/api';
import { userService } from '@/api/user/userApi';
import { useAuthStore } from '@/store/authStore';
import { useFeeInitializer } from '@/hooks/useFeeInitializer';

const Register: React.FC = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState<string | null>(
    null,
  );
  const navigate = useNavigate();
  const { toast } = useToast();
  const { initializeFees } = useFeeInitializer();

  const {
    register: registerField,
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      birthdate: '',
      gender: undefined,
      id: '',
    },
  });

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  const validatePasswordMatch = (
    password: string,
    confirmPassword: string,
  ): string | null => {
    if (!confirmPassword) return null;
    return password !== confirmPassword ? 'Las contraseñas no coinciden' : null;
  };

  useEffect(() => {
    setPasswordMatchError(validatePasswordMatch(password, confirmPassword));
  }, [password, confirmPassword]);

  const onSubmit = async (data: RegisterFormData) => {
    try {
      // Transform FormData to RegistrationPayload
      const payload: ManagerRegistrationPayload = {
        name: data.name,
        email: data.email,
        password: data.password,
        birthdate: new Date(data.birthdate).toISOString(),
        gender: data.gender,
        id: data.id, // CUIT (Tax ID)
      };

      await managerService.register(payload);

      setRegisteredEmail(data.email);
      setIsSuccess(true);

      toast({
        title: 'Registro exitoso',
        description:
          'Tu cuenta ha sido creada correctamente. Serás redirigido al inicio de sesión.',
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      // The account exists as a player: the FUTURE_OWNER role is granted on login, not here.
      // handleGlobalError logs the other branches itself, so only this one needs logError.
      if (getAxiosErrorStatus(error) === 409) {
        logError('Registration error', error);
        toast({
          variant: 'destructive',
          title: 'Ese email ya tiene cuenta en GetMatch',
          description:
            'Iniciá sesión con tu cuenta y te habilitamos el panel de administrador.',
        });
        navigate('/login');
        return;
      }

      handleGlobalError(
        error,
        'Error de registro',
        'No se pudo completar el registro. Por favor, intenta nuevamente.',
      );
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      setIsLoading(true);
      const { accessToken, refreshToken } = await managerService.loginWithGoogle(
        credentialResponse.credential,
      );
      await setAuthTokens(accessToken, refreshToken);

      // Fetch user data and store it
      const userData = await userService.getCurrentUser();
      useAuthStore.setState({ user: userData });

      // Initialize fees after successful authentication
      await initializeFees();

      toast({
        title: 'Registro exitoso',
        description: 'Bienvenido a GetMatch Administrador',
      });

      navigate('/');
    } catch (error) {
      console.error('Google registration error:', error);
      handleGlobalError(
        error,
        'Error de registro con Google',
        'No se pudo registrar con Google. Por favor, intenta nuevamente.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast({
      variant: 'destructive',
      title: 'Error',
      description: 'No se pudo registrar con Google',
    });
  };
  
  if (isSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-primary">
        <Card className="w-full max-w-md bg-white shadow-lg">
          <CardContent className="p-8">
            <div className="mb-8 text-center">
              <img
                src="/assets/images/logo.svg"
                alt="GetMatch Logo"
                className="mx-auto mb-4 h-20 w-auto"
              />
              <h2 className="font-gilroy text-2xl font-bold text-primary">
                ¡Registro Exitoso!
              </h2>
            </div>

            <div className="space-y-6">
              <div className="text-center">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <p className="font-opensans text-sm leading-relaxed text-gray-700">
                  Tu cuenta ha sido creada exitosamente con el email{' '}
                  <span className="font-semibold text-secondary">
                    {registeredEmail}
                  </span>
                  .
                </p>
                <p className="mt-3 font-opensans text-sm text-gray-600">
                  Serás redirigido al inicio de sesión en unos segundos...
                </p>
              </div>

              <Button
                onClick={() => navigate('/login')}
                className="w-full rounded bg-secondary py-3 font-gilroy font-bold text-white transition duration-200 hover:bg-secondary/90"
              >
                Ir al inicio de sesión
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Registration form
  return (
    <div className="flex min-h-screen items-center justify-center bg-primary py-8">
      <Card className="w-full max-w-2xl bg-white shadow-lg">
        <CardContent className="p-8">
          <div className="mb-8 text-center">
            <img
              src="/assets/images/logo.svg"
              alt="GetMatch Logo"
              className="mx-auto mb-4 h-20 w-auto"
            />
            <h2 className="font-gilroy text-2xl font-bold text-primary">
              Registro de Administrador
            </h2>
            <p className="mt-2 font-opensans text-sm text-gray-600">
              Completa el formulario para crear tu cuenta
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Full name */}
              <div className="md:col-span-2">
                <Label
                  htmlFor="name"
                  className="text-sm font-medium text-gray-700"
                >
                  Nombre completo *
                </Label>
                <div className="relative mt-1">
                  <UserIcon
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                  />
                  <Input
                    id="name"
                    {...registerField('name')}
                    placeholder="Ingresa tu nombre completo"
                    className={`pl-10 ${errors.name ? 'border-red-500' : ''}`}
                    disabled={isSubmitting}
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="md:col-span-2">
                <Label
                  htmlFor="email"
                  className="text-sm font-medium text-gray-700"
                >
                  Correo electrónico *
                </Label>
                <div className="relative mt-1">
                  <Mail
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                  />
                  <Input
                    id="email"
                    type="email"
                    {...registerField('email')}
                    placeholder="tu@email.com"
                    className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                    disabled={isSubmitting}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="md:col-span-2">
                <Label
                  htmlFor="password"
                  className="text-sm font-medium text-gray-700"
                >
                  Contraseña *
                </Label>
                <PasswordInput
                  id="password"
                  {...registerField('password')}
                  autoComplete="new-password"
                  placeholder="Mínimo 6 caracteres"
                  wrapperClassName="mt-1"
                  startAdornment={
                    <Lock
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                    />
                  }
                  className={`pl-10 ${errors.password ? 'border-red-500' : ''}`}
                  disabled={isSubmitting}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Confirm password */}
              <div className="md:col-span-2">
                <Label
                  htmlFor="confirmPassword"
                  className="text-sm font-medium text-gray-700"
                >
                  Repetir contraseña *
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  {...registerField('confirmPassword')}
                  autoComplete="new-password"
                  placeholder="Repite la contraseña"
                  wrapperClassName="mt-1"
                  startAdornment={
                    <Lock
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                    />
                  }
                  className={`pl-10 ${errors.confirmPassword || passwordMatchError ? 'border-red-500' : ''}`}
                  disabled={isSubmitting}
                />
                {(errors.confirmPassword || passwordMatchError) && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.confirmPassword?.message || passwordMatchError}
                  </p>
                )}
              </div>

              {/* Birthdate */}
              <div>
                <Label
                  htmlFor="birthdate"
                  className="text-sm font-medium text-gray-700"
                >
                  Fecha de nacimiento *
                </Label>
                <div className="relative mt-1">
                  <Calendar
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                  />
                  <Input
                    id="birthdate"
                    type="date"
                    {...registerField('birthdate')}
                    className={`pl-10 ${errors.birthdate ? 'border-red-500' : ''}`}
                    disabled={isSubmitting}
                  />
                </div>
                {errors.birthdate && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.birthdate.message}
                  </p>
                )}
              </div>

              {/* Gender */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Género *
                </Label>
                <Controller
                  control={control}
                  name="gender"
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger
                        className={`mt-1 ${errors.gender ? 'border-red-500' : ''}`}
                      >
                        <SelectValue placeholder="Seleccionar género" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={Gender.MALE}>Masculino</SelectItem>
                        <SelectItem value={Gender.FEMALE}>Femenino</SelectItem>
                        <SelectItem value={Gender.OTHER}>Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.gender && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.gender.message}
                  </p>
                )}
              </div>

              {/* CUIT */}
              <div className="md:col-span-2">
                <Label
                  htmlFor="id"
                  className="text-sm font-medium text-gray-700"
                >
                  CUIT *
                </Label>
                <div className="relative mt-1">
                  <CreditCard
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                  />
                  <Input
                    id="id"
                    {...registerField('id')}
                    placeholder="XX-XXXXXXXX-X"
                    className={`pl-10 ${errors.id ? 'border-red-500' : ''}`}
                    maxLength={11}
                    onInput={handleCuitInput}
                    disabled={isSubmitting}
                  />
                </div>
                {errors.id && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.id.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Ingresa tu CUIT sin guiones ni espacios (11 dígitos)
                </p>
              </div>
            </div>

            {/* Submit button */}
            <div className="pt-4">
              <Button
                type="submit"
                className="w-full rounded bg-secondary py-3 font-gilroy font-bold text-white transition duration-200 hover:bg-secondary/90"
                disabled={isSubmitting || !isValid || isLoading}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Registrando...
                  </>
                ) : (
                  'Crear cuenta'
                )}
              </Button>
            </div>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 font-opensans text-gray-500">
                o continuar con
              </span>
            </div>
          </div>

          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="outline"
              size="large"
              text="signup_with"
              shape="rectangular"
              width="100%"
            />
          </div>

          {/* Link para volver al login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center font-opensans text-sm text-primary transition-colors hover:text-secondary"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Volver al inicio de sesión
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
